import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkplaceNameDisplay from '../../components/WorkplaceNameDisplay';
import { workplaceApi, documentToolsApi } from '../../services/api';
import './AnnualTrainingPlanPage.css';

interface Workplace {
  id: number;
  title?: string;
  name?: string;
  address?: string;
  dangerClass?: 'low' | 'dangerous' | 'veryDangerous';
  riskLevel?: 'low' | 'dangerous' | 'veryDangerous' | 'very_dangerous';
  sskRegistrationNo?: string;
  sgkNo?: string;
  city?: string;
  district?: string;
  employeeCount?: number;
  contactPerson?: string;
}

interface CategoryItem {
  id: string;
  name: string;
  objective?: string;
  outcomes?: string;
  trainer?: string;
  duration?: string;
  targetAudience?: string;
  isOrientation?: boolean;
  defaultMonths?: number[];
}

interface Category {
  id: string;
  title: string;
  items: CategoryItem[];
}

interface PlanData {
  year: number;
  creationMonth?: number;
  requiredHours: number;
  validityYears: number;
  workplace: {
    id: number;
    name: string;
    address: string;
    riskLevel: string;
    sgkNo: string;
  };
  expert: {
    id: number;
    name: string;
    certificateNo: string;
  } | null;
  doctor: {
    id: number;
    name: string;
    diplomaNo: string;
  } | null;
  employer: {
    name: string;
    title: string;
  };
  categories: Category[];
  matrix: Record<string, Record<number, boolean>>;
}

const RISK_LEVEL_LABEL: Record<string, string> = {
  low: 'Az Tehlikeli (Asgari 8 Saat)',
  dangerous: 'Tehlikeli (Asgari 12 Saat)',
  veryDangerous: 'Çok Tehlikeli (Asgari 16 Saat)',
  very_dangerous: 'Çok Tehlikeli (Asgari 16 Saat)'
};

const REQUIRED_HOURS_MAP: Record<string, number> = {
  low: 8,
  dangerous: 12,
  veryDangerous: 16,
  very_dangerous: 16
};

const MONTH_NAMES = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

const normalizeSearch = (s: string) =>
  (s || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/i̇/g, 'i')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .trim();

function parseHours(durationStr?: string): number {
  if (!durationStr) return 1;
  const match = durationStr.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 1;
}

export const AnnualTrainingPlanPage: React.FC = () => {
  const navigate = useNavigate();
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<number>(1);
  const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [selectedWp, setSelectedWp] = useState<Workplace | null>(null);

  // Plan Data & Matrix State
  const [planLoading, setPlanLoading] = useState<boolean>(false);
  const [plan, setPlan] = useState<PlanData | null>(null);
  const [matrix, setMatrix] = useState<Record<string, Record<number, boolean>>>({});
  const [employerName, setEmployerName] = useState<string>('');
  const [employerTitle, setEmployerTitle] = useState<string>('İşveren / İşveren Vekili');
  const [downloading, setDownloading] = useState<boolean>(false);

  // Inline Consent States
  const [wordConsentOpen, setWordConsentOpen] = useState<boolean>(false);
  const [wordConsentChecked, setWordConsentChecked] = useState<boolean>(false);

  const [pdfConsentOpen, setPdfConsentOpen] = useState<boolean>(false);
  const [pdfConsentChecked, setPdfConsentChecked] = useState<boolean>(false);

  const scrollToTop = () => {
    const body = document.querySelector('.awp-body') as HTMLElement | null;
    if (body) {
      body.scrollTop = 0;
    } else {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
    }
  };

  useEffect(() => {
    scrollToTop();
  }, [step]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchWorkplaces();
  }, []);

  const fetchWorkplaces = async () => {
    try {
      setLoading(true);
      const res = await workplaceApi.getAll();
      const list = (res && res.data) ? res.data : (Array.isArray(res) ? res : []);

      const normalized: Workplace[] = list.map((wp: any) => ({
        id: wp.id,
        name: wp.title || wp.name || `İşyeri #${wp.id}`,
        title: wp.title || wp.name,
        address: wp.address || [wp.district, wp.city].filter(Boolean).join(' / ') || '',
        riskLevel: wp.dangerClass || wp.riskLevel || 'dangerous',
        dangerClass: wp.dangerClass || wp.riskLevel || 'dangerous',
        sgkNo: wp.sgkNo || wp.sskRegistrationNo || '',
        sskRegistrationNo: wp.sgkNo || wp.sskRegistrationNo || '',
        city: wp.city,
        district: wp.district,
        employeeCount: wp.employeeCount,
        contactPerson: wp.contactPerson
      }));

      setWorkplaces(normalized);
    } catch (err) {
      console.error('Workplaces fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredWorkplaces = useMemo(() => {
    const q = normalizeSearch(searchQuery);
    if (!q) return workplaces;
    return workplaces.filter(wp => {
      const nameStr = normalizeSearch(wp.name || wp.title || '');
      const sgkStr = normalizeSearch(wp.sskRegistrationNo || wp.sgkNo || '');
      const cityStr = normalizeSearch(wp.city || '');
      const distStr = normalizeSearch(wp.district || '');
      return nameStr.includes(q) || sgkStr.includes(q) || cityStr.includes(q) || distStr.includes(q);
    });
  }, [workplaces, searchQuery]);

  const handleSelectWorkplace = (wp: Workplace) => {
    setSelectedWp(wp);
    setSearchQuery(wp.name || wp.title || '');
    setDropdownOpen(false);
  };

  const loadPlanForWorkplace = async (wpId: number) => {
    try {
      setPlanLoading(true);
      const data: PlanData = await documentToolsApi.getAnnualTrainingPlan(wpId);
      if (data) {
        setPlan(data);
        setMatrix(data.matrix || {});
        setEmployerName(data.employer?.name || selectedWp?.contactPerson || '');
        setEmployerTitle(data.employer?.title || 'İşveren / İşveren Vekili');
      }
    } catch (err) {
      console.error('Error loading training plan:', err);
    } finally {
      setPlanLoading(false);
    }
  };

  const goToStep2 = () => {
    if (!selectedWp) {
      alert('Lütfen bir işyeri seçiniz.');
      return;
    }
    loadPlanForWorkplace(selectedWp.id);
    setStep(2);
    scrollToTop();
  };

  const creationMonth = plan?.creationMonth || (new Date().getMonth() + 1);
  const m1 = creationMonth;
  const m2 = (creationMonth % 12) + 1;
  const allowedMonths = [m1, m2];

  const totalSelectedHours = useMemo(() => {
    if (!plan || !plan.categories) return 0;
    let sum = 0;
    plan.categories.forEach(cat => {
      cat.items.forEach(item => {
        const itemState = matrix[item.id] || {};
        const isSelected = Object.values(itemState).some(Boolean);
        if (isSelected) {
          sum += parseHours(item.duration);
        }
      });
    });
    return sum;
  }, [plan, matrix]);

  const reqHours = plan?.requiredHours || (selectedWp ? (REQUIRED_HOURS_MAP[selectedWp.dangerClass || selectedWp.riskLevel || 'dangerous'] || 8) : 8);
  const isHoursSufficient = totalSelectedHours >= reqHours;

  const toggleMonth = (itemId: string, month: number) => {
    if (itemId === 'ot_1') {
      alert('⚠️ İşe Başlama ve Oryantasyon Eğitimi mevzuat gereği ilk gün verilmelidir (İlk ay zorunlu ve kilitlidir).');
      return;
    }

    if (!allowedMonths.includes(month)) {
      alert(`⚠️ Yönetmelik gereği tüm eğitimler belge oluşturma tarihinden itibaren en geç 2 ay (${MONTH_NAMES[m1 - 1]} ve ${MONTH_NAMES[m2 - 1]}) içinde tamamlanmalıdır. 61. güne eğitim eklenemez.`);
      return;
    }

    setMatrix(prev => {
      const itemState = { ...(prev[itemId] || {}) };
      itemState[month] = !itemState[month];
      return { ...prev, [itemId]: itemState };
    });
  };

  const handleProceedToStep3 = () => {
    if (!isHoursSufficient) {
      const confirmContinue = window.confirm(
        `⚠️ DİKKAT: Seçtiğiniz toplam eğitim süresi (${totalSelectedHours} Saat), işyerinizin tehlike sınıfı için mevzuatça zorunlu olan asgari ${reqHours} saatten azdır!\n\nEksik konuları tamamlamadan devam etmek istiyor musunuz?`
      );
      if (!confirmContinue) return;
    }
    setStep(3);
    scrollToTop();
  };

  const executeWordDownload = async () => {
    if (!wordConsentChecked) {
      alert('Devam etmek için lütfen İSG mevzuatı onay kutusunu işaretleyiniz.');
      return;
    }

    if (!selectedWp) return;

    try {
      setDownloading(true);
      const year = plan?.year || new Date().getFullYear();
      const safeName = (selectedWp.name || selectedWp.title || 'Isyeri').replace(/[^a-zA-Z0-9_\-]/g, '_');
      const filename = `Yillik_Egitim_Plani_${year}_${safeName}.docx`;

      await documentToolsApi.downloadAnnualTrainingPlanDocx(selectedWp.id, year, matrix, filename);
    } catch (err: any) {
      console.error('Failed to download training docx:', err);
      alert('❌ Word belgesi indirilirken hata oluştu. Lütfen tekrar deneyiniz.');
    } finally {
      setDownloading(false);
    }
  };

  const executePrintPdf = () => {
    if (!pdfConsentChecked) {
      alert('Devam etmek için lütfen İSG mevzuatı onay kutusunu işaretleyiniz.');
      return;
    }
    window.print();
  };

  const wpName = selectedWp?.name || selectedWp?.title || '';
  const wpRisk = (selectedWp?.dangerClass || selectedWp?.riskLevel || 'dangerous') as string;

  return (
    <div className="awp-page">
      {/* Top Header */}
      <div className="awp-header">
        <div className="awp-header-left">
          <span className="awp-header-icon">🎓</span>
          <div>
            <h1 className="awp-title">Yıllık Eğitim Planı</h1>
            <p className="awp-subtitle">Çalışanların İş Sağlığı ve Güvenliği Eğitimlerinin Usul ve Esasları Hakkında Yönetmelik Uyarınca</p>
          </div>
        </div>
        <button
          className="awp-btn-secondary"
          style={{ padding: '6px 14px', fontSize: '12px' }}
          onClick={() => navigate('/documents')}
        >
          ← Belge Yönetimine Dön
        </button>
      </div>

      {/* Stepper Bar */}
      <div className="awp-stepper">
        {[
          { num: 1, title: 'İşyeri Seçimi' },
          { num: 2, title: 'Eğitim Matrisi' },
          { num: 3, title: 'Belge İndir & PDF' }
        ].map((s) => (
          <div
            key={s.num}
            onClick={() => {
              if (s.num === 1) { setStep(1); scrollToTop(); }
              if (s.num === 2 && selectedWp) { setStep(2); scrollToTop(); }
              if (s.num === 3 && selectedWp) { setStep(3); scrollToTop(); }
            }}
            className={`awp-step ${step === s.num ? 'active' : ''} ${step > s.num ? 'done' : ''}`}
          >
            <div className="awp-step-num">{step > s.num ? '✓' : s.num}</div>
            <div className="awp-step-label">{s.title}</div>
          </div>
        ))}
      </div>

      {/* Scrollable Body Content */}
      <div className="awp-body">
        {/* STEP 1: İşyeri Seçimi */}
        {step === 1 && (
          <div className="flex-1 overflow-y-auto">
            <div className="awp-card">
              <h2 className="awp-card-title">İşyeri Seçin</h2>
              <p className="text-xs text-slate-500 mb-4">
                İşyeri ünvanı veya SGK Sicil No girerek arama yapabilirsiniz:
              </p>

              {loading ? (
                <div className="py-8 text-center text-slate-500 text-sm">İşyerleri yükleniyor...</div>
              ) : (
                <div className="awp-wp-search-wrap" ref={searchWrapRef}>
                  <div className="awp-wp-search-box">
                    <span className="awp-wp-search-icon">🔍</span>
                    <input
                      type="text"
                      className="awp-wp-search-input"
                      placeholder="İşyeri adı veya SGK Sicil No ile arayın..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setDropdownOpen(true);
                      }}
                      onFocus={(e) => {
                        e.target.select();
                        setDropdownOpen(true);
                      }}
                      autoComplete="off"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        className="awp-wp-search-clear"
                        title="Temizle"
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedWp(null);
                          setDropdownOpen(true);
                        }}
                      >
                        ✕
                      </button>
                    )}
                    <button
                      type="button"
                      className="awp-wp-search-toggle"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 8px', color: '#64748b', fontSize: '13px' }}
                      title="İşyeri Listesini Aç / Kapat"
                      onClick={() => setDropdownOpen(prev => !prev)}
                    >
                      ▾
                    </button>
                  </div>

                  {/* Dropdown Results */}
                  {dropdownOpen && (
                    <div className="awp-wp-dropdown">
                      {filteredWorkplaces.length === 0 ? (
                        <div className="awp-wp-dropdown-empty">Eşleşen işyeri bulunamadı.</div>
                      ) : (
                        filteredWorkplaces.map((wp) => (
                          <div
                            key={wp.id}
                            className={`awp-wp-dropdown-item ${selectedWp?.id === wp.id ? 'active' : ''}`}
                            onClick={() => handleSelectWorkplace(wp)}
                          >
                            <div className="awp-wp-dropdown-main">
                              <span className="awp-wp-dropdown-name">
                                <WorkplaceNameDisplay name={wp.name || wp.title} />
                              </span>
                              <span className={`awp-risk-badge awp-risk-${wp.dangerClass || wp.riskLevel}`}>
                                {RISK_LEVEL_LABEL[wp.dangerClass || wp.riskLevel || 'dangerous']}
                              </span>
                            </div>
                            <div className="awp-wp-dropdown-sub">
                              <span>SGK: <strong>{wp.sskRegistrationNo || wp.sgkNo || '—'}</strong></span>
                              {(wp.city || wp.district) && <span>{[wp.city, wp.district].filter(Boolean).join(' / ')}</span>}
                              {wp.employeeCount && <span>👥 {wp.employeeCount} Çalışan</span>}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Selected Workplace Preview */}
                  {selectedWp && (
                    <div className="awp-wp-preview">
                      <div className="awp-wp-preview-grid">
                        <div className="awp-wp-preview-item">
                          <span className="awp-wp-preview-label">Seçilen Firma</span>
                          <span className="awp-wp-preview-val">{wpName}</span>
                        </div>
                        <div className="awp-wp-preview-item">
                          <span className="awp-wp-preview-label">SGK Sicil No</span>
                          <span className="awp-wp-preview-val font-mono">{selectedWp.sskRegistrationNo || selectedWp.sgkNo || '—'}</span>
                        </div>
                        <div className="awp-wp-preview-item">
                          <span className="awp-wp-preview-label">Tehlike Sınıfı & Yükümlülük</span>
                          <div>
                            <span className={`awp-risk-badge awp-risk-${wpRisk}`}>
                              {RISK_LEVEL_LABEL[wpRisk] || wpRisk}
                            </span>
                          </div>
                        </div>
                        <div className="awp-wp-preview-item">
                          <span className="awp-wp-preview-label">Adres</span>
                          <span className="awp-wp-preview-val text-xs">{selectedWp.address || 'Adres bilgisi yok'}</span>
                        </div>
                      </div>

                      <button
                        onClick={goToStep2}
                        className="awp-btn-continue"
                      >
                        Devam Et (Eğitim Matrisi) →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 2: Eğitim Matrisi */}
        {step === 2 && selectedWp && (
          <div className="space-y-2">
            <div className="awp-card" style={{ padding: '10px 14px' }}>
              {/* Üst Bilgi Satırı */}
              <div className="flex justify-between items-center flex-wrap gap-2 pb-2 border-b">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">FİRMA:</span>
                  <h2 className="text-sm font-black text-slate-900 leading-none">{wpName}</h2>
                  <span className={`awp-risk-badge awp-risk-${wpRisk}`} style={{ fontSize: '10px', padding: '2px 8px', lineHeight: 1.2 }}>
                    {RISK_LEVEL_LABEL[wpRisk] || wpRisk}
                  </span>
                  
                  {/* Canlı Toplam Saat Rozeti */}
                  <div className={`py-0.5 px-2 rounded border font-bold text-[11px] flex items-center gap-1 ${isHoursSufficient ? 'bg-emerald-50 border-emerald-300 text-emerald-900' : 'bg-amber-50 border-amber-300 text-amber-900'}`}>
                    <span>Toplam:</span>
                    <span className="font-black">{totalSelectedHours} / {reqHours} Saat {isHoursSufficient ? '✅' : '⚠️'}</span>
                  </div>
                </div>

                <div className="text-[11px] text-blue-950 bg-blue-50 border border-blue-200 py-1 px-3 rounded flex items-center gap-1.5 font-medium">
                  <span>⚖️</span>
                  <span>Oryantasyon ilk gün (Min. 4s) • 2 ayda ({MONTH_NAMES[m1 - 1]}-{MONTH_NAMES[m2 - 1]}) min. {reqHours} saat</span>
                </div>
              </div>

              {planLoading ? (
                <div className="p-8 text-center text-slate-500 text-xs">Eğitim matrisi yükleniyor...</div>
              ) : (
                <div className="mt-2 space-y-2">
                  <div className="awp-matrix-table-container">
                    <table className="w-full text-left" style={{ minWidth: '780px' }}>
                      <thead>
                        <tr className="bg-slate-800 text-white font-semibold">
                          <th className="py-1 px-2 border border-slate-700 min-w-[210px] text-[10px]">EĞİTİM KONUSU</th>
                          <th className="py-1 px-1 border border-slate-700 w-24 text-[9.5px]">EĞİTİMCİ</th>
                          <th className="py-1 px-1 border border-slate-700 text-center w-12 text-[9.5px]">SÜRE</th>
                          <th className="py-1 px-1 border border-slate-700 w-24 text-[9.5px]">HEDEF KİTLE</th>
                          {MONTH_NAMES.map((m, idx) => {
                            const isAllowed = allowedMonths.includes(idx + 1);
                            return (
                              <th
                                key={idx}
                                className={`py-1 px-0.5 border border-slate-700 text-center min-w-[28px] w-7 font-mono text-[9.5px] leading-tight ${isAllowed ? 'bg-blue-900 text-amber-300 font-bold' : 'opacity-40 text-slate-400'}`}
                                title={isAllowed ? `${idx + 1}. Ay (İzin Verilen Eğitim Ayı)` : 'Mevzuat gereği 60 günden sonraki aylara eğitim planlanamaz'}
                              >
                                <span>{m.slice(0, 3)}</span>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {plan?.categories.map((category) => (
                          <React.Fragment key={category.id}>
                            <tr className="bg-blue-100/90 text-blue-950 font-bold border-t-2 border-blue-300">
                              <td colSpan={16} className="py-1 px-2 tracking-wide uppercase text-[10px] bg-blue-100/80">
                                <strong style={{ fontWeight: 900, color: '#1e3a5f' }}>{category.title}</strong>
                              </td>
                            </tr>
                            {category.items.map((item) => {
                              const itemMatrix = matrix[item.id] || {};
                              const isOrientation = item.id === 'ot_1';

                              return (
                                <tr key={item.id} className="hover:bg-slate-50 transition border-b border-slate-200">
                                  <td className="py-1 px-2 text-slate-900 font-medium border-r border-slate-200 text-[10.5px] leading-tight">
                                    <div className="flex items-center gap-1.5">
                                      {isOrientation && <span className="text-[11px]">⭐</span>}
                                      <span>{item.name}</span>
                                      {isOrientation && (
                                        <span className="px-1.5 py-0.2 bg-red-100 text-red-800 text-[8.5px] font-black rounded border border-red-200">
                                          Min. 4 saat
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="py-1 px-1 text-slate-600 border-r border-slate-200 text-[10px] truncate max-w-[100px]">
                                    {item.trainer || 'İSG Uzmanı'}
                                  </td>
                                  <td className="py-1 px-0.5 text-center text-slate-800 font-bold border-r border-slate-200 text-[10px] whitespace-nowrap">
                                    {item.duration || '1 saat'}
                                  </td>
                                  <td className="py-1 px-1 text-slate-600 border-r border-slate-200 text-[10px] truncate max-w-[100px]">
                                    {item.targetAudience || 'Tüm Çalışanlar'}
                                  </td>
                                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => {
                                    const checked = !!itemMatrix[m];
                                    const isAllowed = allowedMonths.includes(m);

                                    if (isOrientation) {
                                      if (m === m1) {
                                        return (
                                          <td
                                            key={m}
                                            className="p-0.5 text-center border-r border-slate-200 bg-red-50 select-none cursor-default"
                                            title="İşe başlama oryantasyonu ilk gün verilmek zorundadır (Kilitli)"
                                          >
                                            <span className="inline-flex items-center justify-center w-4 h-4 rounded text-xs font-black" style={{ color: '#b91c1c', fontWeight: 900, fontSize: '13px', lineHeight: 1 }}>
                                              X
                                            </span>
                                          </td>
                                        );
                                      } else {
                                        return (
                                          <td
                                            key={m}
                                            className="p-0.5 text-center border-r border-slate-200 bg-slate-100/60 opacity-30 select-none cursor-not-allowed"
                                            title="Oryantasyon ilk gün dışında verilemez"
                                          >
                                            <span className="text-slate-300 font-bold text-[9px]">-</span>
                                          </td>
                                        );
                                      }
                                    }

                                    if (!isAllowed) {
                                      return (
                                        <td
                                          key={m}
                                          onClick={() => toggleMonth(item.id, m)}
                                          className="p-0.5 text-center border-r border-slate-200 bg-slate-100/60 opacity-30 select-none cursor-not-allowed hover:opacity-50"
                                          title="Yönetmelik gereği tüm eğitimler 2 ay içinde tamamlanmalıdır (Maks. 60 gün)"
                                        >
                                          <span className="text-slate-300 font-bold text-[9px]">•</span>
                                        </td>
                                      );
                                    }

                                    return (
                                      <td
                                        key={m}
                                        onClick={() => toggleMonth(item.id, m)}
                                        className={`p-0.5 text-center cursor-pointer border-r border-slate-200 transition select-none ${
                                          checked ? 'bg-red-50 font-bold' : 'hover:bg-blue-50 bg-blue-50/20'
                                        }`}
                                        title={`${item.name} konusunu ${MONTH_NAMES[m - 1]} ayına eklemek/çıkarmak için tıklayın`}
                                      >
                                        {checked ? (
                                          <span className="inline-flex items-center justify-center w-4 h-4 rounded font-black" style={{ color: '#b91c1c', fontWeight: 900, fontSize: '13px', lineHeight: 1 }}>
                                            X
                                          </span>
                                        ) : (
                                          <span className="text-blue-500 font-bold hover:text-blue-700 transition text-[9px]">•</span>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Alt Buton Grubu */}
                  <div style={{ display: 'flex', gap: '12px', width: '100%', marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setStep(1);
                        scrollToTop();
                      }}
                      className="awp-btn-danger"
                      style={{ flex: '1 1 0%', height: '40px' }}
                    >
                      <span>←</span> İşyeri Seçimi (Geri)
                    </button>

                    <button
                      type="button"
                      onClick={handleProceedToStep3}
                      className="awp-btn-generate"
                      style={{ flex: '1 1 0%', height: '40px' }}
                    >
                      Devam Et (Belge İndir & PDF) <span>→</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 3: Belge İndir & PDF */}
        {step === 3 && selectedWp && (
          <div className="max-w-4xl mx-auto flex-1 overflow-y-auto">
            <div className="awp-card awp-compact-download">
              {/* Üst Başlık Satırı */}
              <div className="awp-step3-header">
                <div>
                  <span className="awp-step3-sub">SEÇİLEN FİRMA</span>
                  <h2 className="awp-step3-company">{wpName}</h2>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold px-2.5 py-0.5 bg-blue-100 text-blue-900 rounded-full">
                    {totalSelectedHours} Saat Eğitim
                  </span>
                  <span className="text-[11px] font-bold px-2.5 py-0.5 bg-emerald-100 text-emerald-800 rounded-full">
                    ✓ Plan Hazır
                  </span>
                </div>
              </div>

              {/* Word ve PDF İndirme Butonları */}
              {!wordConsentOpen && !pdfConsentOpen && (
                <div className="awp-step3-desc-row">
                  <div className="awp-step3-desc-left">
                    <span className="awp-step3-desc-icon">🎓</span>
                    <div>
                      <h4 className="awp-step3-desc-title">
                        Yıllık İSG Eğitim Planı Belgesini İndirin
                      </h4>
                      <p className="awp-step3-desc-text">
                        Yatay formatta, oryantasyon ve 2 aylık eğitim matrisini içeren mevzuata uygun Word (.docx) veya PDF belgesini oluşturun.
                      </p>
                    </div>
                  </div>

                  <div className="awp-step3-btn-row">
                    <button
                      type="button"
                      onClick={() => {
                        setWordConsentOpen(true);
                        setPdfConsentOpen(false);
                      }}
                      className="awp-step3-btn awp-btn-save"
                    >
                      <span>📥</span> Word İndir
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setPdfConsentOpen(true);
                        setWordConsentOpen(false);
                      }}
                      className="awp-step3-btn awp-btn-generate"
                    >
                      <span>🖨️</span> PDF / Yazdır
                    </button>
                  </div>
                </div>
              )}

              {/* Word Onay Formu */}
              {wordConsentOpen && (
                <div className="awp-consent-panel awp-consent-word">
                  <div className="awp-consent-title">
                    <span className="awp-consent-icon">📥</span>
                    <div>
                      <strong>Word Belgesi İndirme Onayı (.docx)</strong>
                      <p>
                        Yıllık İSG Eğitim Planı tablosu, seçtiğiniz konular ve sürelerle oluşturulacaktır. Belgenin mevzuata uygunluğunu onaylayınız.
                      </p>
                    </div>
                  </div>

                  <label className="awp-consent-check">
                    <input
                      type="checkbox"
                      checked={wordConsentChecked}
                      onChange={(e) => setWordConsentChecked(e.target.checked)}
                    />
                    <span>Yukarıdaki mevzuat beyanını okudum ve kabul ediyorum.</span>
                  </label>

                  <div className="awp-consent-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setWordConsentOpen(false);
                        setWordConsentChecked(false);
                      }}
                      className="awp-consent-cancel"
                    >
                      Vazgeç
                    </button>
                    <button
                      type="button"
                      onClick={executeWordDownload}
                      disabled={!wordConsentChecked || downloading}
                      className={`awp-consent-confirm awp-consent-confirm-green ${!wordConsentChecked || downloading ? 'disabled' : ''}`}
                    >
                      <span>📥</span> {downloading ? 'İndiriliyor...' : 'Onayla ve Word İndir'}
                    </button>
                  </div>
                </div>
              )}

              {/* PDF / Yazdır Onay Formu */}
              {pdfConsentOpen && (
                <div className="awp-consent-panel awp-consent-pdf">
                  <div className="awp-consent-title">
                    <span className="awp-consent-icon">🖨️</span>
                    <div>
                      <strong>PDF / Yazdırma Onayı</strong>
                      <p>
                        Belge doğrudan yazdırılacak veya PDF olarak kaydedilmek üzere yazdırma ekranı açılacaktır.
                      </p>
                    </div>
                  </div>

                  <label className="awp-consent-check">
                    <input
                      type="checkbox"
                      checked={pdfConsentChecked}
                      onChange={(e) => setPdfConsentChecked(e.target.checked)}
                    />
                    <span>Yukarıdaki mevzuat beyanını okudum ve kabul ediyorum.</span>
                  </label>

                  <div className="awp-consent-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setPdfConsentOpen(false);
                        setPdfConsentChecked(false);
                      }}
                      className="awp-consent-cancel"
                    >
                      Vazgeç
                    </button>
                    <button
                      type="button"
                      onClick={executePrintPdf}
                      disabled={!pdfConsentChecked}
                      className={`awp-consent-confirm awp-consent-confirm-blue ${!pdfConsentChecked ? 'disabled' : ''}`}
                    >
                      <span>🖨️</span> Onayla ve Yazdır
                    </button>
                  </div>
                </div>
              )}

              {/* İmza Blokları */}
              <div className="awp-step3-sig-section">
                <h4 className="awp-step3-sig-title">
                  BELGE İMZA VE KAŞE BİLGİLERİ
                </h4>
                <div className="awp-step3-sig-grid">
                  <div className="awp-step3-sig-block">
                    <p className="awp-step3-sig-role">HAZIRLAYAN (İSG UZMANI)</p>
                    <p className="awp-step3-sig-name">{plan?.expert ? plan.expert.name : 'İş Güvenliği Uzmanı'}</p>
                    <p className="text-[10px] text-slate-400 mt-1">Kaşe / İmza</p>
                  </div>

                  <div className="awp-step3-sig-block">
                    <p className="awp-step3-sig-role">ONAYLAYAN (İŞVEREN / VEKİLİ)</p>
                    <p className="awp-step3-sig-name">{employerName || selectedWp?.contactPerson || 'İşveren / İşveren Vekili'}</p>
                    <p className="text-[10px] text-slate-400 mt-1">Kaşe / İmza</p>
                  </div>

                  <div className="awp-step3-sig-block">
                    <p className="awp-step3-sig-role">KONTROL / UYGULAMA (HEKİM)</p>
                    <p className="awp-step3-sig-name">{plan?.doctor ? plan.doctor.name : 'İşyeri Hekimi'}</p>
                    <p className="text-[10px] text-slate-400 mt-1">Kaşe / İmza</p>
                  </div>
                </div>
              </div>

              {/* Geri Butonu Satırı */}
              <div className="awp-step3-back-row">
                <button
                  type="button"
                  onClick={() => {
                    setStep(2);
                    scrollToTop();
                  }}
                  className="awp-btn-secondary"
                  style={{ fontSize: '12px', padding: '6px 14px' }}
                >
                  <span>←</span> Eğitim Matrisi (Geri)
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Printable Document for window.print */}
      {selectedWp && (
        <div className="awp-printable-document">
          <table className="print-doc-table">
            <tbody>
              <tr>
                <td colSpan={16} className="print-header-cell">
                  {plan?.year || new Date().getFullYear()} YILI İŞ SAĞLIĞI VE GÜVENLİĞİ YILLIK EĞİTİM PLANI
                </td>
              </tr>
              <tr>
                <td colSpan={16} className="print-banner-cell">
                  İŞYERİ UNVANI: {wpName.toUpperCase()}
                </td>
              </tr>
              <tr>
                <td colSpan={16} className="print-info-cell">
                  <strong>SGK SİCİL NO:</strong> {selectedWp.sskRegistrationNo || selectedWp.sgkNo || '—'} &nbsp;|&nbsp;&nbsp;
                  <strong>TEHLİKE SINIFI:</strong> {RISK_LEVEL_LABEL[wpRisk] || wpRisk} &nbsp;|&nbsp;&nbsp;
                  <strong>ZORUNLU PERİYOT:</strong> {plan?.validityYears || (wpRisk === 'low' ? 3 : wpRisk === 'dangerous' ? 2 : 1)} Yılda Bir ({reqHours} Saat)
                </td>
              </tr>
              <tr style={{ background: '#1f4e79', color: '#ffffff', fontWeight: 'bold', textAlign: 'center' }}>
                <td style={{ textAlign: 'left', minWidth: '180px', color: '#ffffff' }}>EĞİTİM KONUSU</td>
                <td style={{ width: '100px', color: '#ffffff' }}>EĞİTİMCİ</td>
                <td style={{ width: '45px', color: '#ffffff' }}>SÜRE</td>
                <td style={{ width: '100px', color: '#ffffff' }}>HEDEF KİTLE</td>
                {MONTH_NAMES.map((m, idx) => (
                  <td key={idx} style={{ width: '24px', textAlign: 'center', color: '#ffffff' }}>
                    {m.slice(0, 3)}
                  </td>
                ))}
              </tr>
              {plan?.categories.map((cat) => {
                const activeItems = cat.items.filter(item => {
                  const itemMatrix = matrix[item.id] || {};
                  return Object.values(itemMatrix).some(Boolean);
                });
                if (activeItems.length === 0) return null;

                return (
                  <React.Fragment key={cat.id}>
                    <tr className="print-cat-row">
                      <td colSpan={16}>
                        <strong style={{ fontWeight: 900 }}>{cat.title}</strong>
                      </td>
                    </tr>
                    {activeItems.map((item) => {
                      const itemMatrix = matrix[item.id] || {};
                      return (
                        <tr key={item.id}>
                          <td style={{ fontWeight: 600 }}>{item.name}</td>
                          <td>{item.trainer || 'İSG Uzmanı'}</td>
                          <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{item.duration || '1 saat'}</td>
                          <td>{item.targetAudience || 'Tüm Çalışanlar'}</td>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => {
                            const isChecked = !!itemMatrix[m];
                            return (
                              <td
                                key={m}
                                style={{
                                  textAlign: 'center',
                                  fontWeight: 900,
                                  backgroundColor: isChecked ? '#fee2e2' : '#ffffff',
                                  color: isChecked ? '#b91c1c' : '#ffffff',
                                  fontSize: isChecked ? '14px' : undefined
                                }}
                              >
                                {isChecked ? 'X' : ''}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {/* Signature Grid in Print */}
          <div className="print-sig-grid">
            <div className="print-sig-box">
              <div style={{ fontWeight: 'bold', fontSize: '9px', color: '#1e293b' }}>HAZIRLAYAN</div>
              <div style={{ fontSize: '8.5px', color: '#64748b' }}>İş Güvenliği Uzmanı</div>
              <div style={{ fontWeight: 'bold', fontSize: '9px', marginTop: '2px' }}>{plan?.expert ? plan.expert.name : 'İş Güvenliği Uzmanı'}</div>
              <div style={{ marginTop: '12px', fontSize: '8.5px', color: '#64748b' }}>Kaşe / İmza</div>
            </div>

            <div className="print-sig-box">
              <div style={{ fontWeight: 'bold', fontSize: '9px', color: '#1e293b' }}>ONAYLAYAN</div>
              <div style={{ fontSize: '8.5px', color: '#64748b' }}>{employerTitle || 'İşveren / İşveren Vekili'}</div>
              <div style={{ fontWeight: 'bold', fontSize: '9px', marginTop: '2px' }}>{employerName || selectedWp.contactPerson || 'İşveren / İşveren Vekili'}</div>
              <div style={{ marginTop: '12px', fontSize: '8.5px', color: '#64748b' }}>Kaşe / İmza</div>
            </div>

            <div className="print-sig-box">
              <div style={{ fontWeight: 'bold', fontSize: '9px', color: '#1e293b' }}>KONTROL / UYGULAMA</div>
              <div style={{ fontSize: '8.5px', color: '#64748b' }}>İşyeri Hekimi</div>
              <div style={{ fontWeight: 'bold', fontSize: '9px', marginTop: '2px' }}>{plan?.doctor ? plan.doctor.name : 'İşyeri Hekimi'}</div>
              <div style={{ marginTop: '12px', fontSize: '8.5px', color: '#64748b' }}>Kaşe / İmza</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnualTrainingPlanPage;
